import { type ReactNode, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useRouteLoaderData } from 'react-router';

import { CalProvider } from '@calcom/atoms';

import ActionForm from '~/components/ActionForm';
import { getAppGlobals } from '~/components/AppGlobals/utils';
import { useComponentFetcher } from '~/components/ComponentErrorBoundary/useComponentFetcher';
import { usePageContext } from '~/context/usePageContext';
import useBoostAffiliate, { BOOST_AFFILIATE_COOKIE_NAME } from '~/hooks/useBoostAffiliate';
import type { CalComBooking } from '~/interfaces/cal_com';
import type { Product } from '~/interfaces/product';
import type { RootLoaderData } from '~/root';
import { BookCoachingSessionModal } from '~/routes/products/_components/BookCoachingSessionModal';
import { useClientSecretContext } from '~/routes/products/_hooks/useClientSecretConext';
import { CAL_COM_API_URL } from '~/utils/cal_com/constants';

interface BuyNowActionProps {
  product: Product;
  children: ({ isLoading }: { isLoading: boolean }) => ReactNode | ReactNode[];
}

const BuyNowAction = ({ product, children }: BuyNowActionProps) => {
  const { t } = useTranslation();
  const appGlobals = getAppGlobals();
  const { subscription } = useRouteLoaderData('root') as RootLoaderData;
  const { openPageModalType, setOpenPageModalType, visitToken, referralId, cookies } = usePageContext();
  const boostAffiliateRecommendingPublicationId = useBoostAffiliate(cookies[BOOST_AFFILIATE_COOKIE_NAME] || undefined);
  const fetcher = useComponentFetcher();
  const formRef = useRef<HTMLFormElement>(null);
  const { setClientSecret } = useClientSecretContext();

  const isProductArchived = product?.status === 'archived';

  const coachingSessionRedeemableMetadata: {
    slug: string;
    users: {
      username: string;
    }[];
  } = product?.default_product_variant?.redeemable?.metadata;

  const isCoachingSession =
    product?.default_product_variant?.redeemable?.type === 'ProductRedeemables::CoachingSession';
  const coachingSessionProvider = product?.default_product_variant?.redeemable?.provider;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (isProductArchived) {
      e.preventDefault();
      toast.error(t('products.archived_toast_message'));
      return false;
    }

    if (isCoachingSession && coachingSessionProvider === 'cal_com') {
      e.preventDefault();

      if (!coachingSessionRedeemableMetadata.slug || !coachingSessionRedeemableMetadata.users[0].username) {
        toast.error('Something went wrong, please try again');
      } else {
        setOpenPageModalType('booking_modal');
      }

      return false;
    }

    if (!formRef.current) {
      return;
    }

    const formData = new FormData(formRef.current);
    fetcher.submit(formData, {
      method: 'post',
      action: `/products/${product.slug}`,
    });
  };

  const handleBookingSuccess = (bookingData: CalComBooking) => {
    if (!formRef.current) {
      return;
    }

    const formData = new FormData(formRef.current);
    formData.append('booking_data', JSON.stringify(bookingData));
    formData.append('booking_provider', 'cal_com');
    formData.append('booking_attendee_email', bookingData.attendees[0].email);
    fetcher.submit(formData, {
      method: 'post',
      action: `/products/${product.slug}`,
    });
  };

  return (
    <>
      {coachingSessionRedeemableMetadata?.slug && coachingSessionRedeemableMetadata?.users?.[0]?.username && (
        <CalProvider
          clientId={appGlobals.VITE_CAL_OAUTH_CLIENT_ID}
          options={{
            apiUrl: CAL_COM_API_URL,
          }}
        >
          <BookCoachingSessionModal
            onBookingSuccess={handleBookingSuccess}
            shouldDisableConfirmButton={fetcher.state === 'submitting'}
            eventSlug={coachingSessionRedeemableMetadata.slug}
            calComUserName={coachingSessionRedeemableMetadata.users[0].username}
            currentSubscriberEmail={subscription?.email}
            onClose={() => setOpenPageModalType(null)}
            isOpen={openPageModalType === 'booking_modal'}
          />
        </CalProvider>
      )}
      <ActionForm
        fetcher={fetcher}
        formRef={formRef}
        method="post"
        action={`/products/${product.slug}`}
        onSuccess={(data) => {
          if (data.clientSecret && setClientSecret) {
            setClientSecret(data.clientSecret);
            setOpenPageModalType(null);
          }
        }}
        onSubmit={handleSubmit}
        onError={(data) => {
          toast.error(data?.toast?.message || 'Something went wrong, please try again');
        }}
      >
        <input type="hidden" name="product_variant_id" value={product?.default_product_variant?.id || ''} />
        <input hidden name="sent_from_orchid" value="true" />
        {referralId && <input hidden name="ref" value={referralId} onChange={() => null} />}
        {boostAffiliateRecommendingPublicationId && (
          <input hidden name="bhba" value={boostAffiliateRecommendingPublicationId} onChange={() => null} />
        )}
        {visitToken && <input hidden name="visit_token" value={visitToken} onChange={() => null} />}
        <input hidden name="is_js_enabled" defaultValue="true" />
        {children({ isLoading: fetcher.state === 'submitting' })}
      </ActionForm>
    </>
  );
};

export default BuyNowAction;
